import { useCallback } from 'react';

import {
	type FinalCharlieTableRow,
	type StateUpdater,
	type TableState,
	type UtilParameters,
} from '../use-charlie-table';

export const useRowExpansion = <TableDataModel extends object>({
	options,
	getState,
	updateTableState,
}: UtilParameters<TableDataModel>) => {
	const setExpand = useCallback(
		(newExpand: StateUpdater<TableState['expanded']>) => {
			updateTableState('expanded', newExpand, options?.onExpandChange);
		},
		[options?.onExpandChange, updateTableState],
	);

	const toggleExpandedRow = useCallback(
		(row: FinalCharlieTableRow<TableDataModel>) => {
			setExpand((prevExpanded) => {
				if (prevExpanded?.[row.id] === undefined && row.isDefaultExpanded) {
					return {
						...prevExpanded,
						[row.id]: false,
					};
				}
				if (prevExpanded?.[row.id] === undefined) {
					return {
						...prevExpanded,
						[row.id]: true,
					};
				}

				return {
					...prevExpanded,
					[row.id]: !prevExpanded[row.id],
				};
			});
		},
		[setExpand],
	);

	const getIsRowExpanded = useCallback(
		(row: FinalCharlieTableRow<TableDataModel>) => {
			return getState().expanded?.[row.id] ?? row.isDefaultExpanded ?? false;
		},
		[getState],
	);

	//TODO: add callback for getIsRowExpandable to table hook to decide if row is expandable
	return {
		toggleExpandedRow,
		getIsRowExpanded,
		setExpand,
	};
};
